import clsx from 'clsx';
import { format } from 'date-fns';

export type AttendanceTone = 'emerald' | 'rose' | 'amber' | 'blue' | 'slate';

type SummaryLike = {
  expected?: number;
  expected_records?: number;
  total_records?: number;
  total?: number;
  missed?: number;
  present?: number;
  absent?: number;
  late?: number;
  leave?: number;
  school_days?: number;
};

const toneClasses: Record<AttendanceTone, { box: string; value: string; label: string; meta: string }> = {
  emerald: {
    box: 'bg-emerald-50 border-emerald-200',
    value: 'text-emerald-700',
    label: 'text-emerald-700',
    meta: 'text-emerald-600',
  },
  rose: {
    box: 'bg-rose-50 border-rose-200',
    value: 'text-rose-700',
    label: 'text-rose-700',
    meta: 'text-rose-600',
  },
  amber: {
    box: 'bg-amber-50 border-amber-200',
    value: 'text-amber-700',
    label: 'text-amber-700',
    meta: 'text-amber-600',
  },
  blue: {
    box: 'bg-blue-50 border-blue-200',
    value: 'text-blue-700',
    label: 'text-blue-700',
    meta: 'text-blue-600',
  },
  slate: {
    box: 'bg-slate-50 border-slate-200',
    value: 'text-slate-700',
    label: 'text-slate-600',
    meta: 'text-slate-500',
  },
};

const toCount = (value: number | undefined) => Math.max(0, Number(value || 0));

export function getAttendanceDenominator(summary: SummaryLike, fallback = 0) {
  const explicit = summary.expected ?? summary.expected_records;
  if (typeof explicit === 'number' && explicit > 0) {
    return explicit;
  }

  const recorded = summary.total_records ?? summary.total;
  if (typeof recorded === 'number' && recorded > 0) {
    return recorded + toCount(summary.missed);
  }

  const statusTotal =
    toCount(summary.present) +
    toCount(summary.absent) +
    toCount(summary.late) +
    toCount(summary.leave) +
    toCount(summary.missed);

  return statusTotal || fallback;
}

export function getAttendancePercentage(count: number, denominator: number) {
  return denominator > 0 ? ((count / denominator) * 100).toFixed(1) : '0.0';
}

export function isAttendanceSingleDay(args: {
  timeFilterMode?: string;
  startDate?: string;
  endDate?: string;
}) {
  if (args.timeFilterMode === 'day') return true;
  return Boolean(args.startDate && args.endDate && args.startDate === args.endDate);
}

export function getOfficeAttendanceHeadcount(summary: SummaryLike) {
  return toCount(summary.total);
}

export function getOfficeAttendanceDenominator(summary: SummaryLike, singleDay: boolean) {
  if (singleDay) {
    return getOfficeAttendanceHeadcount(summary) || getAttendanceDenominator(summary);
  }
  return getAttendanceDenominator(summary);
}

export function getOfficeAttendanceMissed(summary: SummaryLike, singleDay: boolean) {
  const marked =
    toCount(summary.present) +
    toCount(summary.absent) +
    toCount(summary.late) +
    toCount(summary.leave);
  if (singleDay) {
    return Math.max(0, getOfficeAttendanceHeadcount(summary) - marked);
  }
  return toCount(summary.missed);
}

export function getOfficeMarksCaption(
  summary: SummaryLike,
  opts: { singleDay: boolean; peopleWord: string; unfinished?: boolean }
) {
  const headcount = getOfficeAttendanceHeadcount(summary);
  if (opts.singleDay) {
    return `${headcount} Active`;
  }
  const days = toCount(summary.school_days);
  const base = `${headcount} ${opts.peopleWord} × ${days} school days`;
  return opts.unfinished ? `${base} so far` : base;
}

export function toLocalISODate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseLocalISODate(iso: string) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

export function formatOfficeDayLabel(isoDate: string) {
  return format(parseLocalISODate(isoDate), 'd MMM yyyy');
}

export type OfficeAttendanceHeadingInput = {
  singleDay: boolean;
  periodSubType?: 'monthly' | 'yearly' | 'custom';
  selectedYear?: number;
  selectedMonth?: number;
  customDate?: string;
  startDate?: string;
  endDate?: string;
  now?: Date;
};

export function getOfficeAttendanceHeading(input: OfficeAttendanceHeadingInput): {
  title: string;
  unfinished: boolean;
} {
  const now = input.now ?? new Date();
  const todayIso = toLocalISODate(now);

  if (input.singleDay) {
    const date = input.customDate || input.startDate || todayIso;
    if (date === todayIso) {
      return { title: 'Attendance for today', unfinished: true };
    }
    return { title: `Attendance for ${formatOfficeDayLabel(date)}`, unfinished: false };
  }

  const kind = input.periodSubType ?? 'monthly';

  if (kind === 'monthly') {
    const year = input.selectedYear ?? now.getFullYear();
    const month = input.selectedMonth ?? now.getMonth() + 1;
    const monthLabel = format(new Date(year, month - 1, 1), 'MMMM yyyy');
    const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
    if (isCurrent) {
      return { title: 'Attendance so far this month', unfinished: true };
    }
    return { title: `Attendance for ${monthLabel}`, unfinished: false };
  }

  if (kind === 'yearly') {
    const year = input.selectedYear ?? now.getFullYear();
    if (year === now.getFullYear()) {
      return { title: 'Attendance so far this year', unfinished: true };
    }
    return { title: `Attendance for ${year}`, unfinished: false };
  }

  const start = input.startDate;
  const end = input.endDate;
  if (start && end) {
    const includesToday = start <= todayIso && todayIso <= end;
    if (includesToday) {
      return { title: 'Attendance so far this period', unfinished: true };
    }
    if (start === end) {
      return { title: `Attendance for ${formatOfficeDayLabel(start)}`, unfinished: false };
    }
    const startDate = parseLocalISODate(start);
    const endDate = parseLocalISODate(end);
    const startLabel = format(
      startDate,
      startDate.getFullYear() === endDate.getFullYear() ? 'd MMM' : 'd MMM yyyy'
    );
    return { title: `Attendance for ${startLabel} – ${format(endDate, 'd MMM yyyy')}`, unfinished: false };
  }

  return { title: 'Attendance so far this period', unfinished: true };
}

export function OfficeAttendanceHeading({
  title,
  caption,
}: {
  title: string;
  caption?: string;
}) {
  return (
    <div className="mb-4">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
      {caption ? <p className="mt-1.5 text-base font-medium text-slate-600">{caption}</p> : null}
    </div>
  );
}

export function AttendanceOutOfBlock({
  label,
  count,
  total,
  tone,
  className,
  display = 'people',
}: {
  label: string;
  count: number;
  total: number;
  tone: AttendanceTone;
  className?: string;
  display?: 'people' | 'percent';
}) {
  const classes = toneClasses[tone];
  const percent = getAttendancePercentage(count, total);

  return (
    <div
      data-attendance-display={display}
      className={clsx('rounded-lg border p-3 text-center', classes.box, className)}
    >
      <div className={clsx('text-2xl font-bold tabular-nums leading-tight', classes.value)}>
        {display === 'percent' ? (
          `${percent}%`
        ) : (
          <>
            {count}
            <span className="px-1 text-lg font-semibold text-slate-400"> / </span>
            {total}
          </>
        )}
      </div>
      <div className={clsx('mt-1 text-xs font-semibold uppercase tracking-wide', classes.label)}>
        {label}
      </div>
      {display === 'people' ? (
        <div className={clsx('mt-1 text-xs font-medium', classes.meta)}>
          {percent}%
        </div>
      ) : null}
    </div>
  );
}
